import type { LucideIcon } from "lucide-react"

export enum ChatScope {
  Global = "Global",
  Deal = "Deal",
  Document = "Document",
}

export type ChatLayoutMode = "dock" | "sidebar" | "modal"
export type ChatDockAnchor = "left" | "center" | "right"
export type ChatPanelSize = "medium" | "large"

export type ContextItemType = "contract" | "document" | "person" | "folder" | "page"

export interface ChatContextItem {
  id: string
  type: ContextItemType
  label: string
  value: string
  description?: string
  // Legacy compatibility fields for older chat UI call sites.
  detail?: string
  scope?: ChatScope.Deal | ChatScope.Document
}

export type MessageActionType =
  | "navigate"
  | "highlight"
  | "rating"
  | "copy"
  | "feedback"
  | "custom"

export interface MessageAction {
  id: string
  type: MessageActionType
  label: string
  icon?: LucideIcon
  variant?: "default" | "secondary" | "outline" | "ghost"
  data?: unknown
  onAction?: (action: MessageAction) => void | Promise<void>
}

export interface NavigateAction extends MessageAction {
  type: "navigate"
  data: {
    route?: string
    viewId?: string
    scrollTo?: string
    highlight?: boolean
  }
}

export interface HighlightAction extends MessageAction {
  type: "highlight"
  data: {
    targetId: string
    duration?: number
  }
}

export interface RatingAction extends MessageAction {
  type: "rating"
  data: {
    messageId: string
    value?: "positive" | "negative"
  }
}

export interface CopyAction extends MessageAction {
  type: "copy"
  data: {
    text: string
  }
}

export interface Message {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
  actions?: MessageAction[]
  scope?: ChatScope
  contextItems?: ChatContextItem[]
}

export interface ChatSession {
  id: string
  title: string
  summary?: string
  messages: Message[]
  scope: ChatScope
  createdAt: Date
  updatedAt: Date
  draft: string
  contextItems: ChatContextItem[]
}

export interface SuggestedPrompt {
  label: string
  scope: ChatScope
}

export interface ChatLayoutPreferences {
  mode: ChatLayoutMode
  dockAnchor: ChatDockAnchor
  panelSize: ChatPanelSize
}

export function createNavigateAction(
  label: string,
  route: string,
  options?: Partial<NavigateAction["data"]>
): NavigateAction {
  return {
    id: `nav-${Date.now()}`,
    type: "navigate",
    label,
    variant: "default",
    data: {
      route,
      ...options,
    },
  }
}

export function createRatingAction(messageId: string): RatingAction {
  return {
    id: `rating-${messageId}`,
    type: "rating",
    label: "Rate this response",
    variant: "ghost",
    data: {
      messageId,
    },
  }
}

export function createCopyAction(text: string): CopyAction {
  return {
    id: `copy-${Date.now()}`,
    type: "copy",
    label: "Copy",
    variant: "ghost",
    data: {
      text,
    },
  }
}
